import { CommonPopUpModal, FlexRowBetween, LoaderBar, PageHeader } from 'components'
import { CompanyAddEditModal, CompanyList } from 'page-components'
import { useMemo, useState } from 'react'
import { GetUserListForGroup, GetCustomerList, GetTempleteList, GetCompanyList, GetTimeZoneList, useNotification } from 'hooks'
import { CompanyService, DefaultText, PaginationType, SettingsText, ShowPhotoGpsService } from 'utility'
import { useAuth } from 'context'
import { DesktopMode, MobileMode } from 'layout'
import { AppPrimaryButton } from 'elements'

const saveNameFormats = async (companyId, nameFormats) => {
    if (!companyId || !nameFormats) return

    const tasks = []
    if (nameFormats.inspectionNameConfig) {
        tasks.push(
            CompanyService.updateInspectionNameConfig(
                companyId,
                nameFormats.inspectionNameConfig,
            ),
        )
    }
    if (nameFormats.pdfFilenameConfig) {
        tasks.push(
            CompanyService.updatePdfFilenameConfig(
                companyId,
                nameFormats.pdfFilenameConfig,
            ),
        )
    }

    if (tasks.length) {
        await Promise.all(tasks)
    }
}

export const CompanyScreen = () => {
    const [processing, setProcessing] = useState('')
    const [modalData, setModalData] = useState({})
    const [openCompany, setOpenCompany] = useState(false)
    const { user } = useAuth()
    const { successToast, errorToast } = useNotification()
    const { data, setData, loading, filter, pageChanged, totalCount, refresh } = GetCompanyList()
    const { options: timeZonesList } = GetTimeZoneList()
    const { options: templateOptions } = GetTempleteList()
    const { options: userseOptions } = GetUserListForGroup(PaginationType.All)
    const { options: customerOptions } = GetCustomerList()

    const companyId = user?.CompanyId || user?.Companies?.[0]?.CompanyId

    // Same row the server picks for inspection names: default first, then the oldest row with a time zone.
    const savedTimeZoneRow = useMemo(() => {
        const rows = (data || [])
            .filter(r => r?.TimeZone && String(r.TimeZone).trim())
            .sort((a, b) => (b.IsDefault ? 1 : 0) - (a.IsDefault ? 1 : 0) || (a.SettingId || 0) - (b.SettingId || 0))
        return rows[0] || null
    }, [data])
    const savedTimeZone = savedTimeZoneRow ? String(savedTimeZoneRow.TimeZone).trim() : ''
    const savedTimeZoneSettingId = savedTimeZoneRow?.SettingId || 0

    const remove = async (id) => {
        setProcessing("Processing")
        try {
            await CompanyService.remove(id)
            setData(data.filter(ele => ele.SettingId !== id))
        } catch (error) {
            console.log(error)
        } finally {
            setProcessing("")
        }
    }

    const onClose = async (formdata) => {
        try {
            if (formdata) {
                const { nameFormats, ShowPhotoGps, ...companyFields } = formdata
                setProcessing("Saving")

                if (companyFields?.SettingId) {
                    await CompanyService.update(companyFields)
                } else {
                    await CompanyService.add({
                        ...companyFields,
                        SettingId: 0,
                        CompanyId: companyId,
                    })
                }

                let secondaryError = false
                try {
                    await saveNameFormats(companyId, nameFormats)
                } catch (formatError) {
                    secondaryError = true
                    console.error(formatError)
                    errorToast(
                        typeof formatError?.error === 'string'
                            ? formatError.error
                            : 'Company saved, but name format settings could not be updated.',
                    )
                }

                try {
                    if (typeof ShowPhotoGps === 'boolean') {
                        await ShowPhotoGpsService.setCompany(ShowPhotoGps)
                    }
                } catch (gpsError) {
                    secondaryError = true
                    console.error(gpsError)
                    errorToast(
                        typeof gpsError?.error === 'string'
                            ? gpsError.error
                            : 'Company saved, but GPS caption setting could not be updated.',
                    )
                }

                if (!secondaryError) {
                    successToast(SettingsText.companySaved)
                }
                refresh()
            }
        } catch (error) {
            console.log(error)
            errorToast(
                typeof error?.error === 'string'
                    ? error.error
                    : DefaultText.defaultError,
            )
        } finally {
            setProcessing("")
            setModalData({})
            setOpenCompany(false)
        }
    }

    const editCompany = (data) => {
        setModalData(data)
        setOpenCompany(true)
    }

    return (
        <>
            {(processing || loading) && <LoaderBar content={processing || "Loading"} />}
            <FlexRowBetween>
                <PageHeader header="Company List" hideWeb />
                <MobileMode>
                    <span>
                        <AppPrimaryButton loading={loading} onClick={() => setOpenCompany(true)}>
                            Create
                        </AppPrimaryButton>
                    </span>
                </MobileMode>
            </FlexRowBetween>
            <DesktopMode>
                <div className="col-12">
                    <CompanyAddEditModal
                        onClose={onClose}
                        data={modalData}
                        loading={processing}
                        customerOptions={customerOptions}
                        templateOptions={templateOptions}
                        userseOptions={userseOptions}
                        open
                        timeZonesList={timeZonesList}
                        savedTimeZone={savedTimeZone}
                        savedTimeZoneSettingId={savedTimeZoneSettingId}
                    />
                </div>
            </DesktopMode>
            <MobileMode>
                <CommonPopUpModal open={openCompany} onClose={onClose} title={!modalData.SettingId ? "Create Company" : "Edit company"}>
                    <CompanyAddEditModal
                        onClose={onClose}
                        data={modalData}
                        loading={processing}
                        customerOptions={customerOptions}
                        templateOptions={templateOptions}
                        userseOptions={userseOptions}
                        open={openCompany}
                        timeZonesList={timeZonesList}
                        savedTimeZone={savedTimeZone}
                        savedTimeZoneSettingId={savedTimeZoneSettingId}
                    />
                </CommonPopUpModal>
            </MobileMode>
            <div className="my-3">
                <CompanyList
                    data={data}
                    pageChanged={pageChanged}
                    totalCount={totalCount}
                    filter={filter}
                    remove={remove}
                    onEdit={editCompany}
                />
            </div>
        </>
    )
}
